import React, { useState, useEffect } from 'react'

import Button from './Button'
import Input from './Input'
import Card from './Card'
import { extractTenantInfo } from '@/utils'
import { forcyteService, playgroundService } from '@/services'

import './styles.css'

export const ApiDemo = () => {
  const [serviceStatus, setServiceStatus] = useState({})
  const [token, setToken] = useState(null)
  const [tenantInfo, setTenantInfo] = useState({})
  const [testEndpoint, setTestEndpoint] = useState('/user')
  const [testData, setTestData] = useState('{"name": "Test User"}')
  const [apiResponse, setApiResponse] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    updateServiceStatus()
    updateTenantInfo()
    setToken(forcyteService.getToken())
  }, [])

  const updateServiceStatus = () => {
    setServiceStatus(forcyteService.isConfigured())
  }

  const updateTenantInfo = () => {
    setTenantInfo(extractTenantInfo())
  }

  const handleTestApiCall = async () => {
    setLoading(true)
    setError(null)
    setApiResponse(null)

    try {
      let data = null
      if (testData.trim()) {
        try {
          data = JSON.parse(testData)
        } catch (e) {
          throw new Error('Invalid JSON in test data')
        }
      }

      const response = await playgroundService.getPlayground(testEndpoint, data)
      setApiResponse(response)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleSetToken = () => {
    const token = prompt('Enter authentication token:')
    if (token) {
      forcyteService.setToken(token)
      updateServiceStatus()
    }
  }

  const handleClearToken = () => {
    forcyteService.clearToken()
    updateServiceStatus()
  }

  const handleRefresh = () => {
    forcyteService.refresh()
    updateServiceStatus()
    updateTenantInfo()
  }

  return (
    <section className='d37-api-demo'>
      <div className='d37-api-demo__container'>
        <h2 className='d37-api-demo__title'>D37 API Service Demo</h2>

        <div className='d37-api-demo__grid'>
          {/* Service Status */}
          <Card
            title='Service Status'
            subtitle='Current API service configuration'
          >
            <div className='d37-api-demo__status'>
              <div className='d37-api-demo__status-item'>
                <strong>Base URL:</strong> {serviceStatus.baseURL}
              </div>
              <div className='d37-api-demo__status-item'>
                <strong>Has Token:</strong>{' '}
                {serviceStatus.hasToken ? '✅ Yes' : '❌ No'}
              </div>
              <div className='d37-api-demo__status-item'>
                <strong>Has Tenant ID:</strong>{' '}
                {serviceStatus.hasTenantId ? '✅ Yes' : '❌ No'}
              </div>
              <div className='d37-api-demo__status-item'>
                <strong>Tenant ID:</strong>{' '}
                {serviceStatus.tenantId || 'Not detected'}
              </div>
              <div className='d37-api-demo__status-item'>
                <strong>Token:</strong> {serviceStatus.token || 'Not set'}
              </div>
            </div>

            <div className='d37-api-demo__actions'>
              <Button
                onClick={handleSetToken}
                variant='primary'
                color='primary'
              >
                Set Token
              </Button>
              <Button
                onClick={handleClearToken}
                variant='secondary'
                size='small'
              >
                Clear Token
              </Button>
              <Button onClick={handleRefresh} variant='text' size='small'>
                Refresh
              </Button>
            </div>
          </Card>

          {/* Tenant Information */}
          <Card
            title='Tenant Information'
            subtitle='Hostname and tenant detection'
          >
            <div className='d37-api-demo__tenant-info'>
              <div className='d37-api-demo__status-item'>
                <strong>Hostname:</strong> {tenantInfo.hostname}
              </div>
              <div className='d37-api-demo__status-item'>
                <strong>Current Tenant:</strong>{' '}
                {tenantInfo.currentTenant || 'Not detected'}
              </div>
              <div className='d37-api-demo__status-item'>
                <strong>URL Tenant:</strong>{' '}
                {tenantInfo.urlTenant || 'Not found'}
              </div>
              <div className='d37-api-demo__status-item'>
                <strong>Final Tenant:</strong> {tenantInfo.finalTenant}
              </div>
              <div className='d37-api-demo__status-item'>
                <strong>Valid Format:</strong>{' '}
                {tenantInfo.isValid ? '✅ Yes' : '❌ No'}
              </div>
            </div>
          </Card>

          {/* API Testing */}
          <Card
            title='API Testing'
            subtitle='Test API calls to D37 backend'
            className='d37-api-demo__card--full'
          >
            <div className='d37-api-demo__test-form'>
              <Input
                label='Endpoint'
                value={testEndpoint}
                onChange={e => setTestEndpoint(e.target.value)}
                placeholder='/contact-donors/portal/profile'
              />

              <Input
                label='Test Data (JSON)'
                value={testData}
                onChange={e => setTestData(e.target.value)}
                placeholder='{"name": "Test User"}'
              />

              <Button
                onClick={handleTestApiCall}
                variant='primary'
                size='medium'
                disabled={loading}
                className='d37-api-demo__test-button'
              >
                {loading ? 'Testing...' : 'Test API Call'}
              </Button>
            </div>

            {error && (
              <div className='d37-api-demo__error'>
                <strong>Error:</strong> {error}
              </div>
            )}

            {apiResponse && (
              <div className='d37-api-demo__response'>
                <strong>Response:</strong>
                <pre>{JSON.stringify(apiResponse, null, 2)}</pre>
              </div>
            )}
          </Card>
        </div>
      </div>
    </section>
  )
}
